import { Component, HostListener, NgZone, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';
import Swal from 'sweetalert2';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { CdkDrag, CdkDragDrop, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { AlertService } from '@app/shared/alert.service';
import { SlotDialogComponent, SlotDialogData } from './slot-dialog/slot-dialog.component';
import {
	HrActivity, HrEmployee, HrRequirement, HrSchedule, HrSlot,
	hrColorOf, hrEligibleFor, hrEmployeeNameOf, hrOpeningOf, hrRequirementActivityId, hrResolveActivities, hrScheduleActivityId,
} from '@app/shared/_models/hr';
import { Store, User } from '@app/shared/_models/common';
import {
	EVENTTYP_INSERT, EVENTTYP_UPDATE, HR_GRID_END_MIN, HR_GRID_SNAP_MIN, HR_GRID_SPLIT_MIN,
	HR_GRID_START_MIN, HR_PERSON_COLORS, OBJECTTYP_HR_SCHEDULE,
} from '@app/shared/constants';
import {
	LegalIssue, PresenceSegment, addDays, comparePeriods, crossWeekRestIssues, hoursLabel, legalIssuesForWeek, minutesLabel, mondayOf,
	plannedMinutes, presenceSegments, scheduleAppliesOn, schedulePeriodLabel, schedulePeriodShort,
	slotConflictsOf, weekIndexOf,
} from '../hr-schedule-engine';
import { hourLabel, mergeRanges, timelineWindow } from '../hr-timeline';
import { DragBandVM, EMPTY_DRAG_BAND, dragBandOfRow } from '../hr-drag-band';
import { swapEmployeeSlots, swapSummary } from '../hr-swap';
import { rosterCompare, rosterOrderOf, withRosterSeq } from '../hr-roster';
import { HrPrintService, hrPrintErrorMessage } from '../hr-print.service';
import { SchedulePrintGrid, buildSchedulePrintDoc } from '../hr-schedule-print';
import { PersonPlanningDialogComponent, PersonPlanningDialogData } from './person-planning-dialog/person-planning-dialog.component';
import { pickedDateStr, todayStr } from '@app/shared/time';

export const HR_DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

// Une barre affichée : le créneau + sa géométrie (% de la fenêtre horaire du jour).
export interface BarVM {
	slot: HrSlot;
	left: number;			// %
	width: number;		// %
	label: string;		// « 8h–12h30 » — court, pour tenir DANS la barre
	title: string;		// « 08:00–12:30 · 4h30 » — l'infobulle, où la place ne manque pas
}

// Une ligne de la grille : un employé, ses 7 jours de barres, son total, ses alertes.
export interface GridRow {
	id_employee: number;
	name: string;
	color: string;
	eligible: boolean;			// rôle admis dans ce service + mobilité sur l'établissement
	contract_min: number | null;
	cells: BarVM[][];				// index 0..6 = lundi..dimanche
	total_min: number;
	other_min: number;			// heures planifiées AILLEURS (autres services / établissements, même semaine réelle)
	other_detail: string;		// tooltip du cumul : « Ollioules — Magasin : 8h »
	issues: LegalIssue[];
}

// Un trait de la frise, à chaque heure. `label` non nul = trait appuyé (heure « ronde » de l'échelle).
export interface HourTick {
	pct: number;
	label: string | null;
}

// Une graduation CHIFFRÉE de l'entête d'un jour. Les heures d'ouverture et de fermeture priment
// sur les heures rondes : « 19h » (fermeture) vaut mieux que « 18h » qui ne dit rien.
export interface DayTick {
	pct: number;
	label: string;			// « 9h », « 19h », « 14h30 »
	kind: 'open' | 'close' | 'hour';
}

// Un segment de la bande de présence (géométrie précalculée).
export interface PresenceVM {
	left: number;
	width: number;
	count: number;
	low: boolean;				// effectif sous le minimum exigé à cet instant
	label: string;			// tooltip « 09:00–12:00 : 2 (min 3) »
}


// État d'un glisser en cours sur la grille.
interface DragState {
	mode: 'create' | 'move' | 'start' | 'end';
	wi: number;
	dayIdx: number;
	id_employee: number;
	rect: DOMRect;					// géométrie de la cellule au pointerdown
	originMin: number;			// minute sous le pointeur au départ
	slot: HrSlot;						// créneau manipulé (temporaire en création)
	before: { start_min: number; end_min: number };		// pour restaurer si annulation
	moved: boolean;
}

// Éditeur des horaires types d'UN service — /hr/schedule(/:id).
// On choisit l'établissement puis le service : la grille de CE service se charge (ou se crée).
// Les personnes admissibles (rôle + mobilité) sont proposées ; chaque journée se peint à la
// souris : glisser sur une case = créer la barre, tirer un bord = régler l'heure, glisser la
// barre = déplacer, clic = saisir les heures au clavier. La bande « Présence » montre à chaque
// instant combien de personnes sont là, en rouge sous le minimum exigé (Effectifs minimum).
// Si la personne travaille déjà ailleurs au même moment, le logiciel AVERTIT (jamais n'interdit).
@Component({
	selector: 'app-hr-schedule',
	standalone: true,
	imports: [MenuCommonComponent, ReactiveFormsModule, FormsModule, RouterLink, DragDropModule, NgTemplateOutlet,
		MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule, MatIconModule, MatTabsModule,
		MatDatepickerModule, MatSlideToggleModule, MatTooltipModule, MatCheckboxModule],
	templateUrl: './schedule.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './schedule.component.scss',
})
export class ScheduleComponent implements OnInit, OnDestroy {

	public hrService = inject(HrService);
	private commonService = inject(CommonService);
	private alertService = inject(AlertService);
	private printService = inject(HrPrintService);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private dialog = inject(MatDialog);
	private zone = inject(NgZone);

	public dayLabels = HR_DAY_LABELS;
	public cycleChoices = [1, 2, 3, 4, 5, 6];
	public hoursLabel = hoursLabel;			// utilisé par le template (cumul « ailleurs »)
	// Fenêtre horaire AFFICHÉE — calée sur l'amplitude d'ouverture du service (une demi-heure de
	// marge, pas deux heures de hachures), et qui s'élargit d'elle-même si un créneau dépasse
	// (fermeture 19h, mais on peut demander au personnel de rester jusqu'à 19h15, 22h…).
	public gridStart = HR_GRID_START_MIN;
	public gridEnd = HR_GRID_END_MIN;
	public splitPct = ((HR_GRID_SPLIT_MIN - HR_GRID_START_MIN) / (HR_GRID_END_MIN - HR_GRID_START_MIN)) * 100;
	public hourTicks: HourTick[] = [];
	public dayScales: DayTick[][] = [];		// dayScales[dayIdx] — graduations chiffrées de l'entête

	// Sélection établissement → service → période
	public storeCtrl = new FormControl<number | null>(null);
	public activityCtrl = new FormControl<number | null>(null);
	// Un même service porte PLUSIEURS grilles qui se succèdent dans le temps (fenêtres
	// datstart/datend : « la semaine du 20/07 », puis celle du 27/07…). Le service seul ne désigne
	// donc plus une grille : on se déplace de période en période avec ‹ ›, et le butinage (des
	// centaines de grilles au fil des années) se fait dans « Tous les horaires », qui sait filtrer.
	public periods: HrSchedule[] = [];			// grilles du service, de la plus ancienne à la plus récente
	public periodIndex = 0;								// rang de la grille affichée dans `periods` (-1 = grille neuve)
	public periodLabel = schedulePeriodLabel;
	public periodShort = schedulePeriodShort;
	public creating = false;							// « Nouvel emploi du temps » : on n'attache AUCUNE grille existante
	public stores: Store[] = [];
	public activities: HrActivity[] = [];
	public storeActivities: HrActivity[] = [];

	// Entête du planning chargé
	public headerForm: FormGroup = this.newHeaderForm();
	public schedule: HrSchedule | null = null;		// planning en cours (null tant que rien de sélectionné)
	public slots: HrSlot[] = [];
	public members: number[] = [];
	public showSettings = false;

	public grids: GridRow[][] = [];								// grids[week_index]
	// L'ordre des lignes, tel qu'il est enregistré dans `hr_schedule.data.member_seq` (hr-roster.ts).
	// Il vaut pour TOUTES les semaines du cycle : la place d'une personne dans la grille n'est pas
	// une propriété de sa semaine.
	public rosterOrder: number[] = [];
	public swapLists: string[][] = [];						// ids des cellules de nom connectées, par semaine
	// Le glissé de créneaux : qui l'on a soulevé, et à quoi ressemble la bande qu'on tient.
	public swapSourceId: number | null = null;		// ligne dont les créneaux sont « en l'air »
	public slotBand: DragBandVM = EMPTY_DRAG_BAND;
	public presence: PresenceVM[][][] = [];				// presence[week_index][dayIdx]
	public closed: { left: number; width: number }[][] = [];		// closed[dayIdx] — hors horaires d'ouverture du service
	// Jours de fermeture du service : masqués par défaut (le dimanche d'un magasin n'a rien à
	// faire dans la grille), réaffichés par le toggle « Jours fermés ».
	public closedDay: boolean[] = [false, false, false, false, false, false, false];
	public visibleDayIndices: number[] = [0, 1, 2, 3, 4, 5, 6];
	public showClosedDays = false;
	public weekIndexes: number[] = [0];
	public currentWeekIndex = 0;
	public selectedTab = 0;

	public employees: HrEmployee[] = [];
	public users: User[] = [];
	public availableChips: { id: number; name: string; color: string }[] = [];
	public addMemberCtrl = new FormControl<number | null>(null);
	public allEmployees = false;

	public dirty = false;
	public saving = false;
	public loaded = false;

	private schedules: HrSchedule[] = [];
	private requirements: HrRequirement[] = [];
	private routeSub?: Subscription;
	private headerSub?: Subscription;
	private drag: DragState | null = null;
	private onMove = (e: PointerEvent) => this.zone.run(() => this.dragMove(e));
	private onUp = (e: PointerEvent) => this.zone.run(() => this.dragEnd(e));

	ngOnInit(): void {
		// /hr/schedule/new = créer ; /hr/schedule/:id = éditer CETTE grille ; /hr/schedule = celle en vigueur.
		this.routeSub = this.route.paramMap.subscribe(params => {
			const raw = (params.get('id') ?? '').trim();
			this.creating = raw === 'new';
			this.showSettings = this.showSettings || this.creating;		// nom + période à remplir : on ouvre les réglages
			this.load(this.creating ? 0 : +raw);
		});
		this.storeCtrl.valueChanges.subscribe(() => this.onStoreChange());
		this.activityCtrl.valueChanges.subscribe(id => this.onActivityChange(id));
	}

	ngOnDestroy(): void {
		this.routeSub?.unsubscribe();
		this.headerSub?.unsubscribe();
		this.unbindDrag();
	}

	@HostListener('window:beforeunload', ['$event'])
	onBeforeUnload(e: BeforeUnloadEvent): void {
		if (this.dirty) e.preventDefault();
	}

	private newHeaderForm(): FormGroup {
		return new FormGroup({
			id: new FormControl<number>(0),
			name: new FormControl<string>(''),
			cycle_weeks: new FormControl<number>(1),
			anchor_monday: new FormControl<string | Date | null>(mondayOf(todayStr())),
			datstart: new FormControl<string | Date | null>(null),
			datend: new FormControl<string | Date | null>(null),
			is_active: new FormControl<boolean>(true),
		});
	}

	// ============================================================
	// Chargement initial + sélection établissement / service
	// ============================================================

	private load(id: number): void {
		forkJoin({
			schedules: this.hrService.getSchedules$(),
			employees: this.hrService.getEmployees$(),
			users: this.hrService.getUsers$(),
			stores: this.commonService.getStores(),
			activities: this.hrService.getActivities$(),
			requirements: this.hrService.getRequirements$(),
		}).subscribe(({ schedules, employees, users, stores, activities, requirements }) => {
			this.schedules = schedules;
			this.employees = employees;
			this.users = users;
			this.stores = stores;
			this.requirements = requirements;
			this.activities = hrResolveActivities(activities, schedules, requirements);
			this.loaded = true;

			const sch = schedules.find(s => s.id === id);
			const storeId = sch?.id_store ?? this.storeCtrl.value ?? stores[0]?.id ?? null;
			this.storeCtrl.setValue(storeId, { emitEvent: false });
			this.refreshStoreActivities();
			const actId = sch ? hrScheduleActivityId(sch) : (this.activityCtrl.value ?? this.storeActivities[0]?.id ?? null);
			this.activityCtrl.setValue(actId, { emitEvent: false });
			this.bindActivity(actId, sch?.id ?? 0);		// l'URL désigne LA grille, pas seulement le service
		});
	}

	private refreshStoreActivities(): void {
		const store = this.storeCtrl.value;
		this.storeActivities = this.activities.filter(a => a.id_store === store && a.is_active !== false);
	}

	get currentActivity(): HrActivity | null {
		return this.activities.find(a => a.id === this.activityCtrl.value) ?? null;
	}

	// La grille affichée est-elle celle qui s'applique aujourd'hui ? (pastille « en cours »)
	public get periodIsCurrent(): boolean {
		return !!this.schedule && this.schedule.id > 0 && scheduleAppliesOn(this.schedule, todayStr());
	}

	private onStoreChange(): void {
		this.guardDirty(() => {
			this.refreshStoreActivities();
			const first = this.storeActivities[0]?.id ?? null;
			this.activityCtrl.setValue(first, { emitEvent: false });
			this.bindActivity(first);
		}, () => this.storeCtrl.setValue(this.schedule?.id_store ?? null, { emitEvent: false }));
	}

	private onActivityChange(id: number | null): void {
		this.guardDirty(() => this.bindActivity(id),
			() => this.activityCtrl.setValue(this.schedule ? hrScheduleActivityId(this.schedule) : null, { emitEvent: false }));
	}

	// Période précédente / suivante du même service. On ne déroule jamais une liste qui peut compter
	// des centaines d'entrées : on avance d'un cran, et « Tous les horaires » sert au vrai butinage.
	// On passe par l'URL pour que le lien reste partageable.
	onStepPeriod(step: -1 | 1): void {
		const target = this.periods[this.periodIndex + step];
		if (!target) return;
		this.guardDirty(() => this.router.navigate(['/hr/schedule/' + target.id]), () => { }, 'Changer de période ?');
	}

	get hasPrevPeriod(): boolean { return this.periodIndex > 0; }
	get hasNextPeriod(): boolean { return this.periodIndex >= 0 && this.periodIndex < this.periods.length - 1; }

	// Des modifications non enregistrées ? On confirme avant de changer de grille.
	private guardDirty(go: () => void, revert: () => void, title = 'Changer de service ?'): void {
		if (!this.dirty) { go(); return; }
		Swal.fire({
			title,
			text: 'Les modifications non enregistrées de cette grille seront perdues.',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Changer', cancelButtonText: 'Rester',
		}).then(r => { if (r.isConfirmed) { this.dirty = false; go(); } else revert(); });
	}

	// Premier jour libre après les périodes déjà connues du service : le lendemain du dernier
	// `datend`. Une grille sans fin ne libère rien → on propose aujourd'hui, charge à l'utilisateur
	// de borner l'ancienne (ce que « Nouveaux horaires à partir du… » fait tout seul).
	private nextFreeDay(): string {
		const ends = this.periods.map(s => s.datend).filter((d): d is string => !!d);
		const allBounded = ends.length > 0 && ends.length === this.periods.length;
		return allBounded ? addDays(ends.reduce((a, b) => (a > b ? a : b)), 1) : todayStr();
	}

	// Charge (ou prépare) LA grille demandée du service sélectionné. `wantedId` = celle que vise
	// l'URL ; à défaut on ouvre celle EN VIGUEUR aujourd'hui — et non la première venue. En mode
	// création (« Nouvel emploi du temps »), on n'attache RIEN d'existant : grille vierge, qui
	// démarre au lendemain de la dernière période connue du service.
	private bindActivity(actId: number | null, wantedId = 0): void {
		const activity = this.activities.find(a => a.id === actId) ?? null;
		if (!activity) { this.schedule = null; this.periods = []; this.periodIndex = -1; this.slots = []; this.members = []; this.rebuild(); return; }

		this.periods = this.schedules.filter(s => hrScheduleActivityId(s) === actId).sort(comparePeriods);
		const sch = this.creating ? undefined : (this.periods.find(s => s.id === wantedId)
			?? this.periods.find(s => scheduleAppliesOn(s, todayStr()))
			?? this.periods[0]);
		this.schedule = sch ?? Object.assign(new HrSchedule(), {
			id: 0, name: '', id_store: activity.id_store,
			id_activity: activity.id > 0 ? activity.id : null,
			activity: activity.id < 0 ? ((-activity.id) % 10) : null,
			cycle_weeks: 1, anchor_monday: mondayOf(this.nextFreeDay()), members: [], is_active: true, lnk_slots: [],
			datstart: this.periods.length > 0 ? this.nextFreeDay() : null, datend: null,
		});
		this.periodIndex = sch ? this.periods.indexOf(sch) : -1;

		this.headerForm = this.newHeaderForm();
		this.headerForm.patchValue({
			id: this.schedule.id, name: this.schedule.name, cycle_weeks: this.schedule.cycle_weeks,
			anchor_monday: this.schedule.anchor_monday,
			datstart: this.schedule.datstart, datend: this.schedule.datend,
			is_active: this.schedule.is_active !== false,
		});
		this.slots = (this.schedule.lnk_slots ?? []).map(s => Object.assign(new HrSlot(), s));
		this.members = [...(this.schedule.members ?? [])];
		this.headerSub?.unsubscribe();
		this.headerSub = this.headerForm.valueChanges.subscribe(() => { this.dirty = true; this.rebuild(); });
		this.dirty = false;
		this.selectedTab = 0;
		this.rebuild();
	}

	// ============================================================
	// Grille : lignes, barres, présence, disponibles
	// ============================================================

	private rebuild(): void {
		const cycle = Math.max(1, +(this.headerForm.get('cycle_weeks')?.value ?? 1));
		this.weekIndexes = Array.from({ length: cycle }, (_, i) => i);
		const anchor = pickedDateStr(this.headerForm.get('anchor_monday')?.value) ?? todayStr();
		this.currentWeekIndex = weekIndexOf(todayStr(), anchor, cycle);
		if (this.selectedTab >= cycle) this.selectedTab = 0;

		// La fenêtre est celle de l'OUVERTURE du service (+ une demi-heure de marge), élargie aux
		// créneaux qui débordent : 06:00–21:00 en dur passait la moitié de la largeur en hachures.
		// Repli sur 06:00–21:00 quand le service n'a ni horaires saisis ni créneaux.
		const openings = [1, 2, 3, 4, 5, 6, 7].flatMap(d => hrOpeningOf(this.currentActivity, d));
		const win = timelineWindow(this.slots, openings, HR_GRID_START_MIN, HR_GRID_END_MIN, 30);
		this.gridStart = win.start_min;
		this.gridEnd = win.end_min;
		const span = win.span_min;
		this.splitPct = ((HR_GRID_SPLIT_MIN - this.gridStart) / span) * 100;
		// Trait à chaque heure ; appuyé sur les heures « rondes » de l'échelle (toutes les 3 h sur
		// une large amplitude, toutes les 2 h quand la journée est resserrée).
		const labelEvery = span > 600 ? 3 : 2;
		this.hourTicks = [];
		for (let h = Math.ceil(this.gridStart / 60); h * 60 <= this.gridEnd; h++) {
			this.hourTicks.push({ pct: ((h * 60 - this.gridStart) / span) * 100, label: h % labelEvery === 0 ? h + 'h' : null });
		}

		const rowIds = Array.from(new Set<number>([...this.members, ...this.slots.map(s => s.id_employee)]));
		// L'ORDRE DES LIGNES est une donnée de la grille (`data.member_seq`), pas l'alphabet : un
		// gérant range son équipe comme elle se relaie. L'ancien tri — éligibles d'abord, puis
		// alphabétique — ne sert plus que de secours, pour les lignes qui n'ont pas encore de rang.
		this.rosterOrder = rosterOrderOf(rowIds, this.schedule?.data, rest => rest
			.map(id => this.buildRow(id, 0))
			.sort((a, b) => (+b.eligible - +a.eligible) || a.name.localeCompare(b.name))
			.map(r => r.id_employee));
		const byRoster = rosterCompare(this.rosterOrder);
		this.grids = this.weekIndexes.map(wi => rowIds
			.map(empId => this.buildRow(empId, wi))
			.sort((a, b) => byRoster(a.id_employee, b.id_employee)));
		// Les cellules de nom d'une même semaine sont les seules cibles d'un échange de créneaux :
		// on les connecte par leurs identifiants (jamais par un `cdkDropListGroup`, qui embarquerait
		// aussi la liste des LIGNES et laisserait déposer des créneaux dans un déplacement de ligne).
		this.swapLists = this.weekIndexes.map(wi => this.grids[wi].map(r => this.swapListId(wi, r.id_employee)));
		this.presence = this.weekIndexes.map(wi => [0, 1, 2, 3, 4, 5, 6].map(d => this.buildPresence(wi, d + 1)));
		this.refreshVisibleDays();		// avant le grisage : un jour de fermeture se hachure en entier
		this.closed = [0, 1, 2, 3, 4, 5, 6].map(d => this.buildClosed(d + 1));
		this.dayScales = [0, 1, 2, 3, 4, 5, 6].map(d => this.buildDayScale(d + 1));

		const activity = this.currentActivity;
		this.availableChips = !activity ? [] : this.employees
			.filter(e => e.is_active !== false && !rowIds.includes(e.id))
			.filter(e => hrEligibleFor(activity, e, this.users.find(u => u.id === e.id_user)?.roles))
			.map(e => ({ id: e.id, name: hrEmployeeNameOf(e, this.users), color: hrColorOf(e.id, HR_PERSON_COLORS) }))
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	// Prochain lundi RÉEL dont la semaine correspond à l'indice `wi` de CE cycle — sert à aligner
	// les autres plannings (cycles différents) sur la même semaine réelle pour le cumul « ailleurs ».
	private representativeMondayFor(wi: number): string {
		const cycle = Math.max(1, +(this.headerForm.get('cycle_weeks')?.value ?? 1));
		const anchor = pickedDateStr(this.headerForm.get('anchor_monday')?.value) ?? todayStr();
		const base = mondayOf(todayStr());
		for (let k = 0; k < cycle; k++) {
			const m = addDays(base, k * 7);
			if (weekIndexOf(m, anchor, cycle) === wi) return m;
		}
		return base;
	}

	// Heures planifiées AILLEURS cette semaine-là : les créneaux de l'employé dans les AUTRES
	// plannings actifs, chacun résolu sur la même semaine réelle (son propre cycle).
	private minutesElsewhere(empId: number, monday: string): { total: number; detail: string } {
		const currentId = +(this.headerForm.get('id')?.value ?? 0);
		let total = 0;
		const lines: string[] = [];
		for (const sch of this.schedules) {
			if (sch.id === currentId || !scheduleAppliesOn(sch, monday)) continue;		// hors fenêtre ce jour-là = pas cumulé
			const wi = weekIndexOf(monday, sch.anchor_monday, sch.cycle_weeks);
			const minutes = plannedMinutes((sch.lnk_slots ?? []).filter(s => s.id_employee === empId && s.week_index === wi));
			if (minutes <= 0) continue;
			total += minutes;
			const store = this.stores.find(st => st.id === sch.id_store)?.name ?? '';
			const act = this.activities.find(a => a.id === hrScheduleActivityId(sch))?.name ?? sch.name;
			lines.push(store + ' — ' + act + ' : ' + hoursLabel(minutes));
		}
		return { total, detail: lines.join('\n') };
	}

	private buildRow(empId: number, wi: number): GridRow {
		const emp = this.employees.find(e => e.id === empId);
		const activity = this.currentActivity;
		const cells: BarVM[][] = [[], [], [], [], [], [], []];
		for (const s of this.slots) {
			if (s.id_employee !== empId || s.week_index !== wi) continue;
			if (s.weekday >= 1 && s.weekday <= 7) cells[s.weekday - 1].push(this.toBar(s));
		}
		for (const day of cells) day.sort((a, b) => a.slot.start_min - b.slot.start_min);
		const flat = cells.flat().map(b => b.slot);
		const contractMin = emp?.contract_hours_week != null ? Math.round(emp.contract_hours_week * 60) : null;
		const elsewhere = this.minutesElsewhere(empId, this.representativeMondayFor(wi));
		// Repos quotidien dimanche → lundi : la grille est CYCLIQUE, la semaine qui précède celle-ci
		// est la dernière du cycle (elle-même pour un cycle de 1) — « dimanche 23 h, reprise lundi
		// 6 h » se voit donc ici, ce que le contrôle intra-semaine ne sait pas faire (L3131-1).
		const cycle = Math.max(1, +(this.headerForm.get('cycle_weeks')?.value ?? 1));
		const prevWeek = this.slots.filter(s => s.id_employee === empId && s.week_index === ((wi - 1 + cycle) % cycle));
		return {
			id_employee: empId,
			name: hrEmployeeNameOf(emp, this.users),
			color: hrColorOf(empId, HR_PERSON_COLORS),
			eligible: !activity || !emp ? true : hrEligibleFor(activity, emp, this.users.find(u => u.id === emp.id_user)?.roles),
			contract_min: contractMin,
			cells,
			total_min: plannedMinutes(flat),
			other_min: elsewhere.total,
			other_detail: elsewhere.detail,
			// Les plafonds hebdomadaires portent sur la PERSONNE : ce qu'elle fait ailleurs compte,
			// sinon l'écran afficherait « 40h / 35h » sans le moindre avertissement.
			issues: [
				...legalIssuesForWeek(flat, contractMin, elsewhere.total),
				...crossWeekRestIssues(prevWeek, flat),
			],
		};
	}

	private toBar(slot: HrSlot): BarVM {
		const span = this.gridEnd - this.gridStart;
		const a = Math.max(this.gridStart, Math.min(this.gridEnd, slot.start_min));
		const b = Math.max(this.gridStart, Math.min(this.gridEnd, slot.end_min));
		return {
			slot,
			left: ((a - this.gridStart) / span) * 100,
			width: Math.max(1.2, ((b - a) / span) * 100),
			// « 9h–12h » plutôt que « 09:00–12:00 » : une matinée fait 3 h sur 11 h de frise, la
			// version longue se faisait couper au milieu (« 09:00–12:0 »).
			label: hourLabel(slot.start_min) + '–' + hourLabel(slot.end_min),
			title: minutesLabel(slot.start_min) + '–' + minutesLabel(slot.end_min)
				+ ' · ' + hoursLabel(Math.max(0, slot.end_min - slot.start_min)),
		};
	}

	// Bande de présence d'un jour : effectif à chaque instant vs minimum exigé (Effectifs minimum).
	private buildPresence(wi: number, weekday: number): PresenceVM[] {
		const daySlots = this.slots
			.filter(s => s.week_index === wi && s.weekday === weekday)
			.map(s => ({ start_min: s.start_min, end_min: s.end_min, id_employee: s.id_employee }));
		const segs: PresenceSegment[] = presenceSegments(daySlots, this.gridStart, this.gridEnd);
		const actId = this.activityCtrl.value;
		const reqs = this.requirements.filter(r => hrRequirementActivityId(r) === actId && r.weekday === weekday);
		const span = this.gridEnd - this.gridStart;
		return segs.map(seg => {
			const mid = (seg.start_min + seg.end_min) / 2;
			const min = Math.max(0, ...reqs.filter(r => (r.start_min ?? 0) <= mid && mid < (r.end_min ?? 1440)).map(r => r.min_count));
			return {
				left: ((seg.start_min - this.gridStart) / span) * 100,
				width: ((seg.end_min - seg.start_min) / span) * 100,
				count: seg.count,
				low: seg.count < min,
				label: minutesLabel(seg.start_min) + '–' + minutesLabel(seg.end_min) + ' : ' + seg.count + (min ? (' (minimum ' + min + ')') : ''),
			};
		});
	}

	// Hors horaires d'ouverture du service (Établissements & services) : grisé mais JAMAIS bloqué —
	// rester après la fermeture (19h15…) est un cas normal. Service sans aucun horaire saisi = aucun
	// grisage ; jour de fermeture réaffiché par le toggle = hachuré de bout en bout (sans quoi il
	// passait pour le jour le plus ouvert de la semaine).
	private buildClosed(weekday: number): { left: number; width: number }[] {
		const open = hrOpeningOf(this.currentActivity, weekday);
		if (!open.length) return this.closedDay[weekday - 1] ? [{ left: 0, width: 100 }] : [];
		const span = this.gridEnd - this.gridStart;
		const out: { left: number; width: number }[] = [];
		let cursor = this.gridStart;
		for (const r of open) {
			const a = Math.max(this.gridStart, Math.min(this.gridEnd, r.start_min));
			if (a > cursor) out.push({ left: ((cursor - this.gridStart) / span) * 100, width: ((a - cursor) / span) * 100 });
			cursor = Math.max(cursor, Math.min(this.gridEnd, r.end_min));
		}
		if (cursor < this.gridEnd) out.push({ left: ((cursor - this.gridStart) / span) * 100, width: ((this.gridEnd - cursor) / span) * 100 });
		return out;
	}

	// ============================================================
	// Échelle chiffrée d'une journée — les heures qui COMPTENT d'abord
	// ============================================================
	//
	// Une graduation tous les 3 h affichait « 18h » et pas « 19h », alors que 19h est l'heure de
	// FERMETURE du magasin — le repère que cherche celui qui bâtit la grille. On pose donc d'abord
	// les ouvertures et les fermetures du service, puis on complète aux heures rondes là où il
	// reste de la place. Un libellé n'est retenu que s'il ne chevauche pas ses voisins : c'est ce
	// qui écarte « 18h » quand la fermeture de 19h est déjà là, et non l'inverse.

	private buildDayScale(weekday: number): DayTick[] {
		const span = Math.max(1, this.gridEnd - this.gridStart);
		const kept: DayTick[] = [];
		// Écart minimal entre deux libellés, en % de la colonne : serré entre repères d'ouverture
		// (ils priment), large pour le remplissage (il ne doit jamais tasser l'entête).
		const push = (min: number, kind: DayTick['kind'], gapPct: number) => {
			const pct = ((min - this.gridStart) / span) * 100;
			if (pct < 2 || pct > 98) return;			// collé au bord = chevauche la colonne voisine
			if (kept.some(k => Math.abs(k.pct - pct) < gapPct)) return;
			kept.push({ pct, label: hourLabel(min), kind });
		};

		for (const r of mergeRanges(hrOpeningOf(this.currentActivity, weekday))) {
			push(r.start_min, 'open', 8);
			push(r.end_min, 'close', 8);
		}
		const step = span > 600 ? 3 : 2;
		for (let h = Math.ceil(this.gridStart / 60); h * 60 <= this.gridEnd; h++) {
			if (h % step === 0) push(h * 60, 'hour', 16);
		}
		return kept.sort((a, b) => a.pct - b.pct);
	}

	// ============================================================
	// Jours de fermeture — masqués par défaut
	// ============================================================
	//
	// Un jour sans horaire d'ouverture NI créneau est un jour de fermeture : sa colonne ne servait
	// qu'à rétrécir les autres (et, faute d'horaires, elle n'était même pas hachurée — le dimanche
	// passait pour un jour ouvert). On ne masque QUE si le service a des horaires saisis quelque
	// part : sinon plus aucune colonne, et impossible de peindre le premier créneau.

	private refreshVisibleDays(): void {
		const days = [0, 1, 2, 3, 4, 5, 6];
		const hasHours = days.some(d => hrOpeningOf(this.currentActivity, d + 1).length > 0);
		this.closedDay = days.map(d => hasHours
			&& hrOpeningOf(this.currentActivity, d + 1).length === 0
			&& !this.slots.some(s => s.weekday === d + 1));
		this.visibleDayIndices = days.filter(d => this.showClosedDays || !this.closedDay[d]);
	}

	get closedDaysCount(): number {
		return this.closedDay.filter(c => c).length;
	}

	onToggleClosedDays(show: boolean): void {
		this.showClosedDays = show;
		this.refreshVisibleDays();
	}

	// Le total est celui de la PERSONNE, tous services confondus : « 16h » ici et « +19h ailleurs »
	// en dessous obligeaient à faire l'addition de tête pour savoir si la semaine tenait dans le
	// contrat. On additionne — le détail de ce qui vient d'ailleurs reste en survol.
	totalLabel(row: GridRow): string {
		return hoursLabel(row.total_min + row.other_min)
			+ (row.contract_min != null ? ' / ' + hoursLabel(row.contract_min) : '');
	}

	// « dont 19h ailleurs » + le détail par service. Vide quand tout est sur ce planning : le
	// total se suffit alors à lui-même.
	totalTooltip(row: GridRow): string {
		if (row.other_min <= 0) return '';
		return 'dont ' + hoursLabel(row.other_min) + ' hors de ce planning (non modifiables ici)'
			+ '\n' + hoursLabel(row.total_min) + ' sur ce planning'
			+ (row.other_detail ? '\n' + row.other_detail : '');
	}

	cellIssues(row: GridRow, dayIdx: number): LegalIssue[] {
		return row.issues.filter(i => i.weekday === dayIdx + 1);
	}

	weekIssues(row: GridRow): LegalIssue[] {
		return row.issues.filter(i => i.weekday === null);
	}

	issuesTooltip(issues: LegalIssue[]): string {
		return issues.map(i => i.detail).join('\n');
	}

	// ============================================================
	// Personnes : chips « disponibles », renfort hors profil, retrait
	// ============================================================

	onAddChip(id: number): void {
		if (!this.members.includes(id)) {
			this.members.push(id);
			this.dirty = true;
			this.rebuild();
		}
	}

	get selectableEmployees(): HrEmployee[] {
		const activity = this.currentActivity;
		const present = new Set([...this.members, ...this.slots.map(s => s.id_employee)]);
		return this.employees
			.filter(e => e.is_active !== false && !present.has(e.id))
			.filter(e => this.allEmployees || !activity || hrEligibleFor(activity, e, this.users.find(u => u.id === e.id_user)?.roles))
			.sort((a, b) => hrEmployeeNameOf(a, this.users).localeCompare(hrEmployeeNameOf(b, this.users)));
	}

	employeeName(id: number): string {
		return hrEmployeeNameOf(this.employees.find(e => e.id === id), this.users);
	}

	onAddMember(): void {
		const id = this.addMemberCtrl.value;
		if (!id || this.members.includes(id)) return;
		this.members.push(id);
		this.addMemberCtrl.setValue(null);
		this.dirty = true;
		this.rebuild();
	}

	onRemoveMember(empId: number): void {
		const hasSlots = this.slots.some(s => s.id_employee === empId);
		const doIt = () => {
			this.members = this.members.filter(m => m !== empId);
			this.slots = this.slots.filter(s => s.id_employee !== empId);
			this.dirty = true;
			this.rebuild();
		};
		if (!hasSlots) { doIt(); return; }
		Swal.fire({
			title: 'Retirer ' + this.employeeName(empId) + ' ?',
			text: 'Ses créneaux de ce planning seront supprimés.',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Retirer', cancelButtonText: 'Annuler',
		}).then(r => { if (r.isConfirmed) doIt(); });
	}

	// ============================================================
	// Barres : dessin, déplacement, redimensionnement (pointeur)
	// ============================================================

	private minuteAt(clientX: number, rect: DOMRect): number {
		const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
		const raw = this.gridStart + ratio * (this.gridEnd - this.gridStart);
		return Math.max(this.gridStart, Math.min(this.gridEnd, Math.round(raw / HR_GRID_SNAP_MIN) * HR_GRID_SNAP_MIN));
	}

	onCellPointerDown(e: PointerEvent, wi: number, row: GridRow, dayIdx: number, cell: HTMLElement): void {
		if (e.button !== 0) return;
		e.preventDefault();
		const rect = cell.getBoundingClientRect();
		const origin = this.minuteAt(e.clientX, rect);
		const slot = Object.assign(new HrSlot(), {
			id: 0, id_schedule: +(this.headerForm.get('id')?.value ?? 0),
			id_employee: row.id_employee, week_index: wi, weekday: dayIdx + 1,
			start_min: origin, end_min: Math.min(this.gridEnd, origin + HR_GRID_SNAP_MIN),
		});
		this.drag = { mode: 'create', wi, dayIdx, id_employee: row.id_employee, rect, originMin: origin, slot, before: { start_min: slot.start_min, end_min: slot.end_min }, moved: false };
		this.bindDrag();
	}

	onBarPointerDown(e: PointerEvent, wi: number, row: GridRow, dayIdx: number, bar: BarVM, cell: HTMLElement, mode: 'move' | 'start' | 'end'): void {
		if (e.button !== 0) return;
		e.preventDefault();
		e.stopPropagation();
		const rect = cell.getBoundingClientRect();
		this.drag = {
			mode, wi, dayIdx, id_employee: row.id_employee, rect,
			originMin: this.minuteAt(e.clientX, rect),
			slot: bar.slot, before: { start_min: bar.slot.start_min, end_min: bar.slot.end_min }, moved: false,
		};
		this.bindDrag();
	}

	private bindDrag(): void {
		this.zone.runOutsideAngular(() => {
			window.addEventListener('pointermove', this.onMove);
			window.addEventListener('pointerup', this.onUp);
		});
	}

	private unbindDrag(): void {
		window.removeEventListener('pointermove', this.onMove);
		window.removeEventListener('pointerup', this.onUp);
	}

	private dragMove(e: PointerEvent): void {
		const d = this.drag;
		if (!d) return;
		const cur = this.minuteAt(e.clientX, d.rect);
		if (cur !== d.originMin) d.moved = true;
		const s = d.slot;
		if (d.mode === 'create') {
			s.start_min = Math.min(d.originMin, cur);
			s.end_min = Math.max(Math.min(d.originMin, cur) + HR_GRID_SNAP_MIN, Math.max(d.originMin, cur));
			if (d.moved && !this.slots.includes(s)) this.slots.push(s);		// la barre naît au premier mouvement
		} else if (d.mode === 'move') {
			const delta = cur - d.originMin;
			const len = d.before.end_min - d.before.start_min;
			let start = d.before.start_min + delta;
			start = Math.max(this.gridStart, Math.min(this.gridEnd - len, start));
			s.start_min = start;
			s.end_min = start + len;
		} else if (d.mode === 'start') {
			s.start_min = Math.min(cur, d.before.end_min - HR_GRID_SNAP_MIN);
		} else {
			s.end_min = Math.max(cur, d.before.start_min + HR_GRID_SNAP_MIN);
		}
		this.clampToNeighbours(s);
		this.rebuild();
	}

	// Une barre ne chevauche jamais une autre barre du même employé le même jour :
	// elle bute contre ses voisines.
	private clampToNeighbours(s: HrSlot): void {
		for (const other of this.slots) {
			if (other === s || other.id_employee !== s.id_employee || other.week_index !== s.week_index || other.weekday !== s.weekday) continue;
			if (other.end_min <= s.start_min || other.start_min >= s.end_min) continue;
			if (other.start_min >= (s.start_min + s.end_min) / 2) s.end_min = Math.min(s.end_min, other.start_min);
			else s.start_min = Math.max(s.start_min, other.end_min);
		}
		if (s.end_min - s.start_min < HR_GRID_SNAP_MIN) s.end_min = s.start_min + HR_GRID_SNAP_MIN;
	}

	private dragEnd(_e: PointerEvent): void {
		const d = this.drag;
		this.drag = null;
		this.unbindDrag();
		if (!d) return;

		if (!d.moved) {
			// simple clic : saisie précise au clavier (dialog du jour)
			if (d.mode === 'create') this.openDayDialog(d.wi, d.id_employee, d.dayIdx);
			else this.openDayDialog(d.wi, d.id_employee, d.dayIdx);
			return;
		}
		this.dirty = true;
		this.warnConflicts(d.slot, () => {
			// refus : on retire la barre créée, ou on restaure les heures d'origine
			if (d.mode === 'create') this.slots = this.slots.filter(s => s !== d.slot);
			else { d.slot.start_min = d.before.start_min; d.slot.end_min = d.before.end_min; }
			this.rebuild();
		});
		this.rebuild();
	}

	// La personne travaille-t-elle déjà ailleurs à ce moment-là ? On AVERTIT (jamais interdit).
	private warnConflicts(slot: HrSlot, onCancel: () => void): void {
		const conflicts = slotConflictsOf(slot.id_employee, slot.weekday, slot.start_min, slot.end_min,
			this.schedules, +(this.headerForm.get('id')?.value ?? 0));
		if (!conflicts.length) return;
		const lines = conflicts.slice(0, 4).map(c => {
			const store = this.stores.find(st => st.id === c.schedule.id_store)?.name ?? '';
			const act = this.activities.find(a => a.id === hrScheduleActivityId(c.schedule))?.name ?? c.schedule.name;
			return store + ' — ' + act + ' : ' + this.dayLabels[slot.weekday - 1] + ' ' + minutesLabel(c.slot.start_min) + '–' + minutesLabel(c.slot.end_min);
		});
		Swal.fire({
			title: this.employeeName(slot.id_employee) + ' travaille déjà ailleurs',
			html: lines.join('<br>') + '<br><br>Garder quand même ce créneau ?',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Garder', cancelButtonText: 'Retirer',
		}).then(r => { if (!r.isConfirmed) { onCancel(); } });
	}

	// Saisie précise : la liste des créneaux du jour au clavier + duplication rapide vers d'autres jours.
	private openDayDialog(wi: number, empId: number, dayIdx: number): void {
		const daySlots = this.slots.filter(s => s.id_employee === empId && s.week_index === wi && s.weekday === dayIdx + 1);
		const data: SlotDialogData = {
			title: this.employeeName(empId) + ' — ' + this.dayLabels[dayIdx],
			subtitle: this.weekIndexes.length > 1 ? ('Semaine ' + (wi + 1) + ' du cycle') : undefined,
			slots: daySlots.map(s => ({ start_min: s.start_min, end_min: s.end_min, break_min: s.break_min ?? 0 })),
			dayIdx,
			dayLabels: this.dayLabels,
			openings: hrOpeningOf(this.currentActivity, dayIdx + 1),		// « + » propose l'horaire du magasin
		};
		this.dialog.open(SlotDialogComponent, { data, width: '560px' }).afterClosed().subscribe(result => {
			if (result === undefined) return;
			const rows = result as { start_min: number; end_min: number; break_min?: number; copyDays?: number[] }[];
			const added: HrSlot[] = [];
			const mk = (weekday: number, start_min: number, end_min: number, break_min: number): HrSlot => Object.assign(new HrSlot(), {
				id: 0, id_schedule: +(this.headerForm.get('id')?.value ?? 0),
				id_employee: empId, week_index: wi, weekday, start_min, end_min, break_min,
			});

			// Le jour édité est REMPLACÉ par la liste du dialog.
			this.slots = this.slots.filter(s => !(s.id_employee === empId && s.week_index === wi && s.weekday === dayIdx + 1));
			for (const r of rows) {
				const slot = mk(dayIdx + 1, r.start_min, r.end_min, r.break_min ?? 0);
				this.slots.push(slot);
				added.push(slot);
			}
			// Duplication : le créneau est TAMPONNÉ sur les jours cochés — ce qu'il recouvre s'efface.
			for (const r of rows) {
				for (const day of (r.copyDays ?? [])) {
					if (day === dayIdx + 1 || day < 1 || day > 7) continue;
					this.slots = this.slots.filter(s => !(s.id_employee === empId && s.week_index === wi && s.weekday === day
						&& s.start_min < r.end_min && r.start_min < s.end_min));
					const slot = mk(day, r.start_min, r.end_min, r.break_min ?? 0);
					this.slots.push(slot);
					added.push(slot);
				}
			}
			if (!this.members.includes(empId)) this.members.push(empId);
			this.dirty = true;
			this.warnConflictsMany(added);
			this.rebuild();
		});
	}

	// ============================================================
	// Déplacer une LIGNE : la personne et son emploi du temps changent de place
	// ============================================================
	//
	// Le second geste de la grille, et il ne fait pas du tout la même chose que l'échange
	// ci-dessous — d'où deux poignées, à deux endroits, avec deux aperçus différents :
	//
	//   POIGNÉE DE GAUCHE (celle-ci)  la personne et ses créneaux se déplacent ENSEMBLE, comme on
	//                                 réordonne les livraisons d'une tournée. Rien ne change pour
	//                                 personne d'autre : c'est la mise en page de la grille.
	//   POIGNÉE DU MILIEU (l'autre)   à la limite du nom et des heures : seuls les CRÉNEAUX
	//                                 partent, et ils vont chez quelqu'un d'autre.
	//
	// L'ordre obtenu est enregistré avec la grille (`data.member_seq`) : il survit au rechargement,
	// et l'alphabet ne le rejoue plus par-dessus.
	onRowDrop(event: CdkDragDrop<GridRow[]>): void {
		if (event.previousIndex === event.currentIndex) return;
		const order = (event.container.data ?? []).map(r => r.id_employee);
		moveItemInArray(order, event.previousIndex, event.currentIndex);
		// L'ordre s'écrit dans le `data` de la grille EN MÉMOIRE, pas seulement dans un champ du
		// composant : c'est `data.member_seq` que `rebuild()` relit pour trier, et il rejouerait
		// l'ordre de secours par-dessus le déplacement qu'on vient de faire.
		if (this.schedule) this.schedule.data = withRosterSeq(this.schedule.data, order);
		this.rosterOrder = order;
		this.dirty = true;
		this.rebuild();
	}

	// ============================================================
	// P19 — échange de plannings entre deux salariés, au GLISSER-DÉPOSER
	// ============================================================
	//
	// « Je me suis trompé, cette semaine c'était celle de William, pas de Valentin » : on prend les
	// CRÉNEAUX d'un salarié par la poignée posée à la limite de son nom et de ses heures, et on les
	// lâche sur un autre — les deux semaines s'échangent. Même geste que les livraisons d'une
	// tournée (`logistic/delruns`), mêmes primitives du CDK.
	//
	// Ce qui suit la souris est la BANDE DES CRÉNEAUX, pas le nom : la personne, elle, ne bouge
	// pas de sa ligne. L'aperçu par défaut du CDK (le nom dans un cadre) racontait l'inverse, et
	// c'est exactement ce qu'on croyait faire en le regardant.
	//
	// Le geste a remplacé une double boîte de dialogue (choisir avec qui, puis confirmer) : deux
	// écrans pour une correction qu'on fait dix fois par planning, et qui obligeaient à retrouver
	// un nom dans une liste alors qu'on l'a sous les yeux, une ligne plus bas.
	//
	// AUCUNE CONFIRMATION, et c'est réfléchi : l'échange est sa PROPRE INVERSE — refaire le geste
	// dans l'autre sens rend la grille à son état d'avant — et rien ne part au serveur tant qu'on
	// n'a pas enregistré. Le détail de ce qui a bougé est annoncé après coup, pas demandé avant.
	//
	// L'échange porte sur les créneaux THÉORIQUES de la semaine de cycle AFFICHÉE (l'onglet où l'on
	// a lâché) — on corrige la grille, pas l'historique. L'ancienne boîte de dialogue, elle,
	// travaillait toujours sur la semaine COURANTE du cycle, même ouverte depuis l'onglet d'une
	// autre semaine : le geste dit désormais lui-même sur quelle semaine il porte.

	// Ce qu'on emporte est la LIGNE DE CRÉNEAUX elle-même, à la taille de la grille : elle se
	// décolle de sa ligne et monte ou descend jusqu'à celle qui doit la recevoir (hr-drag-band.ts).
	// Une vignette réduite à côté du curseur ne disait pas ce geste-là — on croyait déplacer une
	// personne. Les cases-jour seules : le nom, lui, ne bouge pas de sa ligne.
	onSlotsGrab(ev: PointerEvent): void {
		this.slotBand = dragBandOfRow(ev.currentTarget, 'td.slot-cell .day-cell', ev.clientX, ev.clientY) ?? this.slotBand;
	}

	// La ligne d'où partent les créneaux s'efface le temps du glissé : ses heures sont dans la bande
	// qu'on tient, elles ne doivent pas rester dessinées en double.
	// `zone.run` : le CDK annonce le DÉPART du glissé hors de la zone Angular (à la différence du
	// dépôt) — sans cela, l'affichage n'apprendrait la nouvelle qu'au prochain événement venu.
	onSlotsLift(row: GridRow): void {
		this.zone.run(() => this.swapSourceId = row.id_employee);
	}

	onSlotsDropped(): void {
		this.zone.run(() => this.swapSourceId = null);
	}

	// Chaque cellule de nom est à la fois une poignée et une cible. Ne reste qu'à interdire de se
	// lâcher sur soi-même — un échange avec soi ne veut rien dire, et CDK n'annulerait pas le geste.
	public canSwapInto = (drag: CdkDrag<GridRow>, drop: CdkDropList<GridRow>): boolean =>
		drag.data?.id_employee !== drop.data?.id_employee;

	// L'identifiant d'une cellule-cible. Il porte la semaine : on n'échange qu'au sein d'un onglet.
	swapListId(weekIndex: number, empId: number): string {
		return 'hr-swap-' + weekIndex + '-' + empId;
	}

	onSwapDrop(event: CdkDragDrop<GridRow>, weekIndex: number): void {
		const from = event.item.data as GridRow | undefined;
		const to = event.container.data;
		// Lâché dans le vide, ou sur sa propre ligne : il n'y a rien à échanger.
		if (!from || !to || from.id_employee === to.id_employee) return;

		const result = swapEmployeeSlots(this.slots, from.id_employee, to.id_employee, weekIndex);
		if (result.movedA === 0 && result.movedB === 0) {
			this.alertService.info(swapSummary(result, from.name, to.name));
			return;
		}

		this.slots = result.slots;
		// Les deux doivent figurer au roster, sinon l'un disparaîtrait de la grille.
		for (const id of [from.id_employee, to.id_employee]) if (!this.members.includes(id)) this.members.push(id);
		this.dirty = true;
		this.rebuild();
		this.alertService.success(
			swapSummary(result, from.name, to.name) + ' Refaites le geste en sens inverse pour revenir en arrière.');
	}

	// Avertissement AGRÉGÉ : tous les conflits des créneaux qui viennent d'être posés, en une fois.
	private warnConflictsMany(added: HrSlot[]): void {
		const currentId = +(this.headerForm.get('id')?.value ?? 0);
		const lines: string[] = [];
		const conflicted: HrSlot[] = [];
		for (const slot of added) {
			const conflicts = slotConflictsOf(slot.id_employee, slot.weekday, slot.start_min, slot.end_min, this.schedules, currentId);
			if (!conflicts.length) continue;
			conflicted.push(slot);
			for (const c of conflicts.slice(0, 2)) {
				const store = this.stores.find(st => st.id === c.schedule.id_store)?.name ?? '';
				const act = this.activities.find(a => a.id === hrScheduleActivityId(c.schedule))?.name ?? c.schedule.name;
				lines.push(this.dayLabels[slot.weekday - 1] + ' : déjà ' + store + ' — ' + act + ' ' + minutesLabel(c.slot.start_min) + '–' + minutesLabel(c.slot.end_min));
			}
		}
		if (!conflicted.length) return;
		Swal.fire({
			title: this.employeeName(conflicted[0].id_employee) + ' travaille déjà ailleurs',
			html: lines.slice(0, 6).join('<br>') + '<br><br>Garder quand même ces créneaux ?',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Garder', cancelButtonText: 'Retirer',
		}).then(r => {
			if (r.isConfirmed) return;
			this.slots = this.slots.filter(s => !conflicted.includes(s));
			this.rebuild();
		});
	}

	// ============================================================
	// Impression des horaires types — le document qu'on affiche dans l'entreprise
	// ============================================================
	//
	// On imprime la grille TELLE QU'ELLE EST À L'ÉCRAN, modifications non enregistrées comprises :
	// c'est ce qui est sous les yeux qu'on veut relire sur papier avant de valider. Le document
	// sort une grille par semaine du cycle, plus le calendrier du roulement (hr-schedule-print).
	onPrint(): void {
		if (!this.schedule) return;
		const today = todayStr();
		const doc = buildSchedulePrintDoc({
			storeName: this.stores.find(s => s.id === this.storeCtrl.value)?.name ?? '',
			printedAt: today.slice(8, 10) + '/' + today.slice(5, 7) + '/' + today.slice(0, 4),
			today,
			grids: [this.printGrid()],
		});
		// Rendu par le SERVEUR (POST /hr/print) : la fenêtre s'ouvre dans le prolongement du clic.
		this.printService.print(doc).subscribe({
			error: (e) => this.alertService.error(hrPrintErrorMessage(e)),
		});
	}

	// La grille courante mise à plat pour l'impression. Les valeurs viennent du FORMULAIRE (et non
	// de `this.schedule`) : rotation et période non encore enregistrées doivent s'imprimer telles
	// qu'on vient de les saisir.
	private printGrid(): SchedulePrintGrid {
		const form = (k: string) => this.headerForm.get(k)?.value;
		const activity = this.currentActivity;
		// Les lignes de la semaine 1 donnent le roster affiché — il vaut pour tout le cycle.
		const rows = this.grids[0] ?? [];
		return {
			name: form('name') ?? this.schedule?.name ?? '',
			storeName: this.stores.find(s => s.id === this.storeCtrl.value)?.name ?? '',
			activityName: activity?.name ?? '',
			cycle_weeks: Math.max(1, +(form('cycle_weeks') ?? 1)),
			anchor_monday: pickedDateStr(form('anchor_monday')) ?? mondayOf(todayStr()),
			datstart: pickedDateStr(form('datstart')),
			datend: pickedDateStr(form('datend')),
			is_active: form('is_active') !== false,
			slots: this.slots.map(s => ({
				id_employee: s.id_employee,
				week_index: s.week_index,
				weekday: s.weekday,
				start_min: s.start_min,
				end_min: s.end_min,
				break_min: s.break_min ?? 0,
			})),
			members: rows.map(r => {
				const emp = this.employees.find(e => e.id === r.id_employee);
				return {
					id: r.id_employee,
					name: r.name,
					color: r.color,
					contract_min: r.contract_min,
					// Modulation : le contrat change d'une semaine du cycle à l'autre (34 h / 36 h).
					cycle_contract_min: emp?.cycle_hours?.length ? emp.cycle_hours.map(h => Math.round(h * 60)) : null,
				};
			}),
			// Les horaires d'OUVERTURE du service en tête de colonne : la grille se lit alors
			// « ouvert 9h–19h, tenu par… » plutôt que comme une suite de créneaux hors sol.
			openHours: [1, 2, 3, 4, 5, 6, 7].map(d => mergeRanges(hrOpeningOf(activity, d))
				.map(o => minutesLabel(o.start_min) + ' – ' + minutesLabel(o.end_min))
				.join(', ')),
		};
	}

	// Le planning de LA personne, tous services et établissements confondus (« l'autre sens »).
	onPersonClick(row: GridRow): void {
		const data: PersonPlanningDialogData = {
			id_employee: row.id_employee,
			name: row.name,
			color: row.color,
			contract_min: row.contract_min,
			schedules: this.schedules,
			activities: this.activities,
			stores: this.stores,
			pendingSlots: this.slots,
			pendingScheduleId: +(this.headerForm.get('id')?.value ?? 0),
			pendingAnchor: pickedDateStr(this.headerForm.get('anchor_monday')?.value) ?? todayStr(),
			pendingCycle: Math.max(1, +(this.headerForm.get('cycle_weeks')?.value ?? 1)),
		};
		this.dialog.open(PersonPlanningDialogComponent, { data, width: '560px' });
	}

	// ============================================================
	// Copie de semaine (cycles > 1)
	// ============================================================

	public copyTargetCtrl = new FormControl<number | null>(null);

	onCopyWeek(fromWi: number): void {
		const toWi = this.copyTargetCtrl.value;
		if (toWi === null || toWi === fromWi) return;
		const doIt = () => {
			this.slots = this.slots.filter(s => s.week_index !== toWi);
			for (const s of this.slots.filter(x => x.week_index === fromWi).slice()) {
				this.slots.push(Object.assign(new HrSlot(), { ...s, id: 0, week_index: toWi }));
			}
			this.copyTargetCtrl.setValue(null);
			this.dirty = true;
			this.rebuild();
		};
		if (!this.slots.some(s => s.week_index === toWi)) { doIt(); return; }
		Swal.fire({
			title: 'Remplacer la semaine ' + (toWi + 1) + ' ?',
			text: 'Ses créneaux actuels seront remplacés par ceux de la semaine ' + (fromWi + 1) + '.',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Remplacer', cancelButtonText: 'Annuler',
		}).then(r => { if (r.isConfirmed) doIt(); });
	}

	// ============================================================
	// Enregistrement
	// ============================================================

	onSubmit(): void {
		if (this.saving || !this.schedule) return;
		const activity = this.currentActivity;
		if (!activity) { this.alertService.error('Choisissez l\'établissement et le service.'); return; }
		const v = this.headerForm.value;
		const cycle = Math.max(1, +v.cycle_weeks);
		const anchorPicked = pickedDateStr(v.anchor_monday);
		if (!anchorPicked) { this.alertService.error('Choisissez la date de début du cycle.'); return; }

		const storeName = this.stores.find(s => s.id === activity.id_store)?.name ?? '';
		const sch = new HrSchedule();
		sch.id = +(v.id ?? 0);
		sch.name = (v.name ?? '').trim() || (storeName + ' — ' + activity.name);
		sch.id_store = activity.id_store;
		sch.id_activity = activity.id > 0 ? activity.id : null;
		sch.activity = activity.id < 0 ? ((-activity.id) % 10) : null;
		sch.cycle_weeks = cycle;
		sch.anchor_monday = mondayOf(anchorPicked);
		const dsPicked = pickedDateStr(v.datstart);
		const dePicked = pickedDateStr(v.datend);
		if (dsPicked && dePicked && dePicked < dsPicked) { this.alertService.error('La fin de validité précède le début.'); return; }
		sch.datstart = dsPicked;
		sch.datend = dePicked;
		sch.members = Array.from(new Set([...this.members, ...this.slots.map(s => s.id_employee)]));
		sch.is_active = !!v.is_active;
		sch.lnk_slots = this.slots.filter(s => s.week_index < cycle && sch.members.includes(s.id_employee));
		// L'ORDRE DES LIGNES voyage avec la grille (`data.member_seq`). On repart du `data` chargé :
		// le JSONB peut porter autre chose, et l'écraser ferait perdre ses réglages à qui les y a mis.
		// On ne garde que les rangs des membres réellement enregistrés, sinon la carte enflerait
		// d'un id à chaque personne retirée.
		sch.data = withRosterSeq(this.schedule?.data, this.rosterOrder.filter(id => sch.members.includes(id)));

		this.saving = true;
		this.hrService.postHrEvent(sch.id === 0 ? EVENTTYP_INSERT : EVENTTYP_UPDATE, OBJECTTYP_HR_SCHEDULE, this.hrService.buildSchedulePayload(sch))
			.subscribe({
				next: (res) => {
					this.saving = false;
					this.dirty = false;
					this.alertService.success('Planning enregistré');
					const newId = (sch.id === 0 && res?.data?.id) ? +res.data.id : sch.id;
					// recharge la liste des plannings (conflits inter-services à jour) et suit l'id
					if (sch.id === 0 && newId) this.router.navigate(['/hr/schedule/' + newId]);
					else this.load(newId);
				},
				error: (e) => {
					this.saving = false;
					this.alertService.error(hrErrorMessage(e));
				},
			});
	}

	// « Nouveaux horaires à partir du… » : borne la grille courante (datend = veille) et ouvre une
	// COPIE qui démarre à la date choisie, rotation recalée sur ce lundi. Parti pris borne-à-borne
	// (un seul EDT par jour) — voir hr-schedule-engine.scheduleAppliesOn. La grille doit être
	// enregistrée et sans modif en attente : on chaîne UPDATE (ancienne) puis INSERT (nouvelle).
	onReplaceFrom(): void {
		const currentId = +(this.headerForm.get('id')?.value ?? 0);
		if (currentId <= 0) { this.alertService.error('Enregistrez d\'abord ce planning avant de le remplacer.'); return; }
		if (this.dirty) { this.alertService.error('Enregistrez vos modifications avant de créer les nouveaux horaires.'); return; }
		const base = this.schedules.find(s => s.id === currentId);
		if (!base) return;

		Swal.fire({
			title: 'Nouveaux horaires à partir du…',
			input: 'date',
			inputValue: base.datend ? addDays(base.datend, 1) : mondayOf(todayStr()),
			showCancelButton: true, confirmButtonText: 'Créer', cancelButtonText: 'Annuler',
			inputValidator: (val) => !val ? 'Choisissez une date'
				: (base.datstart && val <= base.datstart ? 'La date doit suivre le début des horaires actuels' : null),
		}).then(r => {
			if (r.isConfirmed && r.value) this.applyReplacement(base, r.value as string);
		});
	}

	private applyReplacement(base: HrSchedule, start: string): void {
		// 1) borne l'ancienne grille à la veille de la bascule (les créneaux restent inchangés)
		const predecessor = Object.assign(new HrSchedule(), base, { datend: addDays(start, -1) });
		// 2) clone : même service, cycle, créneaux et roster ; rotation recalée sur `start`, fenêtre ouverte
		const successor = Object.assign(new HrSchedule(), base, {
			id: 0,
			anchor_monday: mondayOf(start),
			datstart: start,
			datend: null,
			lnk_slots: (base.lnk_slots ?? []).map(s => Object.assign(new HrSlot(), s, { id: 0, id_schedule: 0 })),
		});

		this.saving = true;
		this.hrService.postHrEvent(EVENTTYP_UPDATE, OBJECTTYP_HR_SCHEDULE, this.hrService.buildSchedulePayload(predecessor)).subscribe({
			next: () => this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_SCHEDULE, this.hrService.buildSchedulePayload(successor)).subscribe({
				next: (res) => {
					this.saving = false;
					this.alertService.success('Nouveaux horaires créés à partir du ' + start);
					const newId = res?.data?.id ? +res.data.id : 0;
					if (newId) this.router.navigate(['/hr/schedule/' + newId]);
					else this.load(base.id);
				},
				// Le contexte (création / bornage) reste : il dit LAQUELLE des deux écritures a échoué.
				error: (e) => { this.saving = false; this.alertService.error('Création des nouveaux horaires : ' + hrErrorMessage(e)); },
			}),
			error: (e) => { this.saving = false; this.alertService.error('Bornage des horaires actuels : ' + hrErrorMessage(e)); },
		});
	}

	onOpenList(): void {
		this.router.navigate(['/hr/schedules']);
	}
}
